import { useState, useMemo, useCallback, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  FolderTree,
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
  RefreshCw,
  FileText,
  Folder,
  FolderOpen,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { ObjectGroupAccessDialog } from "@/components/portal/groups/ObjectGroupAccessDialog";
import { useGroupAccessDeepLink } from "@/components/portal/groups/useGroupAccessDeepLink";
import { BulkGroupAccessDialog } from "@/components/portal/groups/BulkGroupAccessDialog";
import { groupsApi } from "@/services/api/groups";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { platformApi, Category } from "@/services/api/platform";
import {
  DraggableTree,
  TreeItemRow,
} from "@/components/ui/draggable-tree";

export const Route = createFileRoute("/portal/knowledgebase-file/categories")({
  component: CategoriesPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { groupAccess?: string; groupAccessName?: string } => ({
    groupAccess:
      typeof search.groupAccess === "string" && search.groupAccess.length > 0
        ? search.groupAccess
        : undefined,
    groupAccessName:
      typeof search.groupAccessName === "string" && search.groupAccessName.length > 0
        ? search.groupAccessName
        : undefined,
  }),
});

function CategoriesPage() {
  return <CategoriesPageContent />;
}

function CategoriesPageContent() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can, isAdmin, bypassesGroups } = usePermissions();
  const canManageGroups = can("edit", "groups") || isAdmin;
  const canCreateCategories = can("create", "kb-file");
  const canEditCategories = can("edit", "kb-file");
  const canDeleteCategories = isAdmin;
  const canRestructure = isAdmin;
  const [loading, setLoading] = useState(true);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  // Server-reported count, so the deep link can tell "not on this page" from "not there".
  // The fetch below asks for 500; this is what proves that ceiling was actually enough.
  const [categoriesTotal, setCategoriesTotal] = useState(0);
  // A failed load leaves both of the above at their empty defaults, which is
  // indistinguishable from "this org has no categories" — so the deep link must not act
  // on it. The page itself only toasts, so nothing else recorded this.
  const [loadFailed, setLoadFailed] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
    parent_id: "",
  });
  const [editCategory, setEditCategory] = useState<Category | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [groupAccessTarget, setGroupAccessTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [bulkGroupAccessOpen, setBulkGroupAccessOpen] = useState(false);
  const [categoryPermissions, setCategoryPermissions] = useState<
    Map<string, { group_name: string; role: string }[]>
  >(new Map());

  const organisationId = selectedOrganisation?.organisationId;

  // Deep link from the group Object Permissions tab: ?groupAccess=<category id>
  const { groupAccess, groupAccessName } = Route.useSearch();
  useGroupAccessDeepLink({
    groupAccess,
    groupAccessName,
    // One fetch of up to 500 — complete for every real org, but compared rather than
    // assumed so the 501st category degrades to the link's name instead of a false
    // "not found".
    // AND `bypassesGroups`: this list is filtered server-side by the caller's own object
    // permissions unless they bypass, so for a non-bypass caller "complete" still omits
    // objects that exist. Reporting those as absent is a false, unrecoverable not-found.
    listIsComplete: allCategories.length >= categoriesTotal && bypassesGroups,
    ready: !loading && !loadFailed && !!organisationId,
    canManageGroups,
    items: allCategories,
    getId: (category) => category.id,
    getName: (category) => category.name,
    onMatch: setGroupAccessTarget,
  });

  const loadCategories = useCallback(async () => {
    if (!token || !organisationId) return;
    setLoading(true);
    try {
      const response = await platformApi.listCategories(token, {
        limit: 500,
        organisation_id: organisationId,
      });
      setAllCategories(response.items || []);
      setCategoriesTotal(response.total ?? 0);
      setLoadFailed(false);
    } catch (error) {
      console.error("Failed to load categories:", error);
      toast.error("Failed to load categories");
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token, organisationId]);

  useEffect(() => {
    if (token && organisationId) {
      loadCategories();
    }
  }, [token, organisationId, loadCategories]);

  const fetchCategoryPermissions = useCallback(async () => {
    // cp answers by-object only for org_owner / org_admin / platform_admin (BYPASS_ROLES,
    // `_require_grant_visibility`); anyone else gets a 403 that trips Platform Health (web #1444).
    if (!token || !organisationId || !bypassesGroups) return;
    try {
      const resp = await groupsApi.listObjectPermissionsByObject(token, organisationId, {
        service_name: "rag",
        object_type: "category",
      });
      const grouped = new Map<string, { group_name: string; role: string }[]>();
      for (const item of resp.items) {
        const existing = grouped.get(item.object_id) ?? [];
        existing.push({ group_name: item.group_name, role: item.role });
        grouped.set(item.object_id, existing);
      }
      setCategoryPermissions(grouped);
    } catch {
      // Non-critical
    }
  }, [token, organisationId, bypassesGroups]);

  useEffect(() => {
    fetchCategoryPermissions();
  }, [fetchCategoryPermissions]);

  const filteredCategories = useMemo(() => {
    if (!searchTerm) return allCategories;
    const search = searchTerm.toLowerCase();
    return allCategories.filter(
      (cat) =>
        cat.name.toLowerCase().includes(search) ||
        cat.description?.toLowerCase().includes(search) ||
        cat.path?.toLowerCase().includes(search),
    );
  }, [allCategories, searchTerm]);

  const handleMove = useCallback(
    async (itemId: string, newParentId: string | null, newIndex: number) => {
      if (!token || !organisationId) return;

      const previousCategories = [...allCategories];
      setAllCategories((prev) =>
        prev.map((cat) =>
          cat.id === itemId
            ? { ...cat, parent_id: newParentId, sort_order: newIndex }
            : cat,
        ),
      );

      try {
        await platformApi.updateCategory(token, itemId, {
          parent_id: newParentId,
          sort_order: newIndex,
        });
        toast.success("Category moved successfully");
        loadCategories();
      } catch (error) {
        console.error("Failed to move category:", error);
        toast.error("Failed to move category");
        setAllCategories(previousCategories);
      }
    },
    [token, organisationId, allCategories, loadCategories],
  );

  const handleCreateCategory = async () => {
    if (!token || !organisationId) return;
    if (!newCategory.name.trim()) {
      toast.error("Category name is required");
      return;
    }
    setSaving(true);
    try {
      await platformApi.createCategory(token, {
        name: newCategory.name,
        description: newCategory.description,
        parent_id: newCategory.parent_id || undefined,
        organisation_id: organisationId,
      });
      toast.success("Category created successfully");
      setCreateDialogOpen(false);
      setNewCategory({ name: "", description: "", parent_id: "" });
      loadCategories();
    } catch (error) {
      console.error("Failed to create category:", error);
      toast.error("Failed to create category");
    } finally {
      setSaving(false);
    }
  };

  const handleEditCategory = async () => {
    if (!token || !editCategory) return;
    if (!editCategory.name.trim()) {
      toast.error("Category name is required");
      return;
    }
    setSaving(true);
    try {
      await platformApi.updateCategory(token, editCategory.id, {
        name: editCategory.name,
        description: editCategory.description,
      });
      toast.success("Category updated successfully");
      setEditDialogOpen(false);
      setEditCategory(null);
      loadCategories();
    } catch (error) {
      console.error("Failed to update category:", error);
      toast.error("Failed to update category");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!token || !categoryToDelete) return;
    setDeleting(true);
    try {
      await platformApi.deleteCategory(token, categoryToDelete.id);
      toast.success("Category deleted successfully");
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      loadCategories();
    } catch (error) {
      console.error("Failed to delete category:", error);
      toast.error("Failed to delete category");
    } finally {
      setDeleting(false);
    }
  };

  const openEditDialog = (category: Category) => {
    setEditCategory({ ...category });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (category: Category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const stats = useMemo(() => {
    const rootCount = allCategories.filter((c) => !c.parent_id).length;
    return {
      total: allCategories.length,
      totalDocuments: allCategories.reduce(
        (sum, cat) => sum + cat.document_count,
        0,
      ),
      rootCategories: rootCount,
    };
  }, [allCategories]);

  const rootCategories = useMemo(
    () => allCategories.filter((c) => !c.parent_id),
    [allCategories],
  );

  const defaultExpandedIds = useMemo(
    () => rootCategories.map((c) => c.id),
    [rootCategories],
  );

  if (loading && allCategories.length === 0) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FolderTree className="h-6 w-6" />
            Categories
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Drag and drop to reorganize categories for{" "}
            {selectedOrganisation?.organisationName || "your organisation"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadCategories}
            disabled={loading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          {canManageGroups && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkGroupAccessOpen(true)}
              disabled={allCategories.length === 0}
            >
              <ShieldCheck className="mr-2 h-4 w-4" />
              Bulk Group Access
            </Button>
          )}
          {canCreateCategories && (
            <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Category
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Categories
            </CardTitle>
            <FolderTree className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              {stats.rootCategories} root categories
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Documents
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalDocuments}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Hierarchy Depth
            </CardTitle>
            <Folder className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.max(
                ...allCategories.map(
                  (c) => (c.path?.split("/").length || 1) - 2,
                ),
                0,
              ) + 1}
            </div>
            <p className="text-xs text-muted-foreground">levels deep</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Category Tree</CardTitle>
              <CardDescription className="text-xs">
                Drag categories to reorganize. Drop on a category to make it a
                child.
              </CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FolderTree className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>
                {searchTerm
                  ? "No categories match your search"
                  : "No categories found"}
              </p>
              <p className="text-xs mt-2">
                Create a category to organize your documents
              </p>
            </div>
          ) : searchTerm ? (
            <div className="space-y-1">
              {filteredCategories.map((category) => (
                <div
                  key={category.id}
                  className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50"
                >
                  <Folder className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{category.name}</div>
                    <div className="text-xs text-muted-foreground font-mono truncate">
                      {category.path}
                    </div>
                  </div>
                  <GroupBadges permissions={categoryPermissions.get(category.id)} />
                  <Badge variant="outline" className="text-xs">
                    {category.document_count} docs
                  </Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canEditCategories && (
                        <DropdownMenuItem
                          onClick={() => openEditDialog(category)}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                      )}
                      {canManageGroups && (
                        <DropdownMenuItem
                          onClick={() =>
                            setGroupAccessTarget({
                              id: category.id,
                              name: category.name,
                            })
                          }
                        >
                          <ShieldCheck className="h-4 w-4 mr-2" />
                          Group Access
                        </DropdownMenuItem>
                      )}
                      {canDeleteCategories && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => openDeleteDialog(category)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          ) : (
            <DraggableTree
              items={allCategories}
              idKey="id"
              parentKey="parent_id"
              sortKey="sort_order"
              onMove={canRestructure ? handleMove : () => {}}
              allowReparent={canRestructure}
              defaultExpandedIds={defaultExpandedIds}
              indentPx={24}
              renderItem={(item, node, props) => (
                <TreeItemRow
                  dragHandleProps={canRestructure ? props.dragHandleProps : {}}
                  isExpanded={props.isExpanded}
                  hasChildren={props.hasChildren}
                  toggleExpand={props.toggleExpand}
                  className={props.isOver ? "bg-primary/5" : ""}
                >
                  <div className="flex items-center gap-2">
                    {props.hasChildren ? (
                      <FolderOpen className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    ) : (
                      <Folder className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item.name}</div>
                      {item.description && (
                        <div className="text-xs text-muted-foreground truncate">
                          {item.description}
                        </div>
                      )}
                    </div>
                    <GroupBadges permissions={categoryPermissions.get(item.id)} />
                    <Badge variant="outline" className="text-xs flex-shrink-0">
                      {item.document_count}
                    </Badge>
                    {props.hasChildren && (
                      <Badge
                        variant="secondary"
                        className="text-xs flex-shrink-0"
                      >
                        {node.children.length} sub
                      </Badge>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 flex-shrink-0 opacity-0 group-hover:opacity-100"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canEditCategories && (
                          <DropdownMenuItem onClick={() => openEditDialog(item)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                        )}
                        {canCreateCategories && (
                          <DropdownMenuItem
                            onClick={() => {
                              setNewCategory({
                                name: "",
                                description: "",
                                parent_id: item.id,
                              });
                              setCreateDialogOpen(true);
                            }}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add Child
                          </DropdownMenuItem>
                        )}
                        {canRestructure && item.parent_id && (
                          <DropdownMenuItem
                            onClick={() => handleMove(item.id, null, 0)}
                          >
                            <FolderTree className="mr-2 h-4 w-4" />
                            Move to Root
                          </DropdownMenuItem>
                        )}
                        {canManageGroups && (
                          <DropdownMenuItem
                            onClick={() =>
                              setGroupAccessTarget({
                                id: item.id,
                                name: item.name,
                              })
                            }
                          >
                            <ShieldCheck className="h-4 w-4 mr-2" />
                            Group Access
                          </DropdownMenuItem>
                        )}
                        {canDeleteCategories && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => openDeleteDialog(item)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TreeItemRow>
              )}
            />
          )}
        </CardContent>
      </Card>

      {/* Create Category Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Category</DialogTitle>
            <DialogDescription>Add a new document category.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input
                placeholder="e.g., Financial Reports"
                value={newCategory.name}
                onChange={(e) =>
                  setNewCategory({ ...newCategory, name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Brief description of this category..."
                value={newCategory.description}
                onChange={(e) =>
                  setNewCategory({
                    ...newCategory,
                    description: e.target.value,
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Parent Category</Label>
              <Select
                value={newCategory.parent_id || "root"}
                onValueChange={(value) =>
                  setNewCategory({
                    ...newCategory,
                    parent_id: value === "root" ? "" : value,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select parent..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="root">Root (No Parent)</SelectItem>
                  {rootCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateCategory} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Category"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
            <DialogDescription>Update the category details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input
                value={editCategory?.name || ""}
                onChange={(e) =>
                  setEditCategory((prev) =>
                    prev ? { ...prev, name: e.target.value } : null,
                  )
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editCategory?.description || ""}
                onChange={(e) =>
                  setEditCategory((prev) =>
                    prev ? { ...prev, description: e.target.value } : null,
                  )
                }
              />
            </div>
            {editCategory?.path && (
              <div className="space-y-2">
                <Label>Path</Label>
                <Input
                  value={editCategory.path}
                  disabled
                  className="bg-muted font-mono text-sm"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditCategory} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{categoryToDelete?.name}
              &quot;? This action cannot be undone.
              {categoryToDelete && categoryToDelete.document_count > 0 && (
                <span className="block mt-2 text-amber-600">
                  Warning: This category has {categoryToDelete.document_count}{" "}
                  document(s) associated with it.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={handleDeleteCategory}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Group Access Dialog (single category) */}
      {groupAccessTarget && (
        <ObjectGroupAccessDialog
          serviceName="rag"
          objectType="category"
          objectId={groupAccessTarget.id}
          objectName={groupAccessTarget.name}
          open={!!groupAccessTarget}
          onOpenChange={(open) => {
            if (!open) { setGroupAccessTarget(null); fetchCategoryPermissions(); }
          }}
        />
      )}

      {/* Bulk Group Access Dialog */}
      <BulkGroupAccessDialog
        serviceName="rag"
        objectType="category"
        items={allCategories.map((c) => ({ id: c.id, name: c.name }))}
        open={bulkGroupAccessOpen}
        onOpenChange={setBulkGroupAccessOpen}
        onComplete={() => fetchCategoryPermissions()}
      />
    </div>
  );
}

const MAX_VISIBLE_BADGES = 2;

function GroupBadges({ permissions }: { permissions?: { group_name: string; role: string }[] }) {
  if (!permissions || permissions.length === 0) return null;

  const visible = permissions.slice(0, MAX_VISIBLE_BADGES);
  const remaining = permissions.length - MAX_VISIBLE_BADGES;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex flex-wrap gap-1 flex-shrink-0">
        {visible.map((p, i) => (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-xs cursor-default">
                {p.group_name}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>{p.role}</TooltipContent>
          </Tooltip>
        ))}
        {remaining > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="text-xs cursor-default">
                +{remaining}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {permissions.slice(MAX_VISIBLE_BADGES).map((p) => `${p.group_name} (${p.role})`).join(", ")}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
